import {Injectable, TemplateRef, ApplicationRef, Renderer2, RendererFactory2} from '@angular/core';
import {MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {ComponentType, OverlayContainer} from "@angular/cdk/overlay";
import {ConfirmationDialogComponent} from "../../components/confirmation-dialog/confirmation-dialog.component";
import {
  CONFIRMATION_DIALOG_CLASS,
  ConfirmationDialogColor,
  ConfirmationDialogData,
  ConfirmationDialogSignal
} from "../../components/confirmation-dialog/confirmation-dialog.model";
import {TranslateService} from "@ngx-translate/core";
import {filter, Subject} from "rxjs";
import {NavigationEnd, Router} from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ValgDialogService {
  private readonly VALG_MODAL_OVERLAY_CONTAINER_CLASS = 'modal-generic__overlay-container'
  private readonly PENDING_CHANGES_GUARD_DIALOG_TITLE_KEY = 'dialog.can_deactivate_confirmation_dialog.title'
  private readonly PENDING_CHANGES_GUARD_DIALOG_DESCRIPTION_KEY = 'dialog.can_deactivate_confirmation_dialog.description'
  private readonly LEAVE_PAGE_KEY = 'dialog.can_deactivate_confirmation_dialog.leave_page'
  private renderer: Renderer2;
  private openDialogSet = new Set<string>();

  constructor(
      private readonly matDialog: MatDialog, 
      private readonly overlayContainer: OverlayContainer,
      private readonly translateService: TranslateService,
      router: Router,
      private app: ApplicationRef,
      rendererFactory: RendererFactory2
  ) {
    
    this.renderer = rendererFactory.createRenderer(null, null);

    router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.matDialog.closeAll();
    })
  }

  open<T>(component: ComponentType<T> | TemplateRef<T>, configs?: MatDialogConfig): MatDialogRef<T>{
    const ref = this.dialogRefKey(component, configs);
    if(this.openDialogSet.has(ref)) {
      throw Error("Dialog of this componentType is already open")
    }
    
    this.openDialogSet.add(ref);
    this.renderer.setAttribute(this.app.components[0].location.nativeElement, 'inert', '');
    this.overlayContainer.getContainerElement().classList.add(this.VALG_MODAL_OVERLAY_CONTAINER_CLASS)
    const rootElement = this.renderer.selectRootElement(document);
    const focusedElement = rootElement.querySelector(':focus');
    const dialogRef = this.matDialog.open(component, configs);
    
    dialogRef.afterClosed().subscribe(() => {
      this.openDialogSet.delete(ref);
      this.overlayContainer.getContainerElement().classList.remove(this.VALG_MODAL_OVERLAY_CONTAINER_CLASS)    
      this.renderer.removeAttribute(this.app.components[0].location.nativeElement, 'inert');
      focusedElement?.focus();
    });
    
    return dialogRef;
  }

  openPendingChangesGuardDialog(canDeactivateResult: Subject<boolean>, descriptionKey: string = this.PENDING_CHANGES_GUARD_DIALOG_DESCRIPTION_KEY) {
    const data: ConfirmationDialogData = {
      title: this.PENDING_CHANGES_GUARD_DIALOG_TITLE_KEY,
      description: descriptionKey,
      color: ConfirmationDialogColor.Light,
      affirmText: this.LEAVE_PAGE_KEY
    }
    const dialogRef = this.open(
        ConfirmationDialogComponent, {
          panelClass: CONFIRMATION_DIALOG_CLASS,
          data: data
        });

    dialogRef.afterClosed().subscribe((signal: ConfirmationDialogSignal) => {
      if (signal === ConfirmationDialogSignal.Continue) {
        canDeactivateResult.next(true);
      } else {
        canDeactivateResult.next(false);
      }
      canDeactivateResult.complete();
    });
  }

  openForbiddenDialog(type: string, releaseOnly = false, isDeleted = false, isRevoked = false): MatDialogRef<ConfirmationDialogComponent> {
    const dialogKey = "create_forbidden_entity_dialog";
    const isTypeNeutralGender = type === 'list_coalition' || type === 'electoral_coalition';
    const config =  {
      panelClass: CONFIRMATION_DIALOG_CLASS,
      autoFocus: 'first-tabbable',
      data: {
        title: this.getDialogTitle(type, dialogKey, isTypeNeutralGender),
        description: this.getDialogDescription(type, dialogKey, releaseOnly, isDeleted, isRevoked, isTypeNeutralGender),
        affirmText: "dialog.ok",
        color: ConfirmationDialogColor.Light,
        hideCancelButton: true
      } as ConfirmationDialogData
    };
    return this.open(ConfirmationDialogComponent, config);
  }

  private getSupporterAction(isDeleted: boolean, isRevoked: boolean, isReleased: boolean) {
    if (isDeleted) {
      return ".deleted"
    }
    else if (isRevoked) {
      return ".revoked";
    }
    else if (isReleased){
      return ".release"
    }
    return "";
  }

  private getSupporterCause(
      isDeleted: boolean,
      isRevoked: boolean,
      dialogKey: string
  ): string {
    if (isDeleted) {
      return this.translateService.instant(dialogKey + ".has_been_deleted");
    } else if (isRevoked) {
      return this.translateService.instant(dialogKey + ".has_been_revoked");
    }
    return "";
  }

  openSupporterRegistrationForbiddenDialog(
      isDeleted = false,
      isRevoked = false,
      isReleased = false,
      shouldShowCause = false
  ): MatDialogRef<ConfirmationDialogComponent> {
    const dialogKey = "supporter_registration_forbidden_dialog";
    const title = this.translateService.instant(dialogKey + ".title");
    const cause = shouldShowCause ? this.getSupporterCause(isDeleted, isRevoked, dialogKey) : "";
    const action = this.getSupporterAction(isDeleted, isRevoked, isReleased);
    const description = this.translateService.instant(dialogKey + ".description", {
      cause,
      action: this.translateService.instant(dialogKey + action)
    });

    const config = {
      panelClass: CONFIRMATION_DIALOG_CLASS,
      autoFocus: 'first-tabbable',
      data: {
        title,
        description,
        affirmText: "dialog.ok",
        color: ConfirmationDialogColor.Light,
        hideCancelButton: true
      } as ConfirmationDialogData
    };

    return this.open(ConfirmationDialogComponent, config);
  }


  private getDialogTitle(type: string, dialogKey: string, isTypeNeutralGender: boolean){
    return this.translateService.instant(dialogKey + ".title",
        {
          newGendered: this.translateService.instant(dialogKey + "." + (isTypeNeutralGender ? 'new_neutral_gender' : 'new_common_gender')),
          type: this.translateService.instant(dialogKey + ".entity." + type),
        });
  }

  private getAction(releaseOnly: boolean, isDeleted: boolean, isRevoked: boolean){
    if (isDeleted) {
      return ".deleted"
    }
    else if (isRevoked) {
      return ".revoked";
    }
    else {
      return releaseOnly ? ".release": ".approve_and_release";
    }
  }

  private getDialogDescription(
      type: string,
      dialogKey: string,
      releaseOnly: boolean,
      isDeleted: boolean,
      isRevoked: boolean,
      isTypeNeutralGender: boolean
  ){
    const action = this.getAction(releaseOnly, isDeleted, isRevoked);
    return this.translateService.instant(
        dialogKey + ".description",
        {
          aGendered: this.translateService.instant(dialogKey + "." + (isTypeNeutralGender ? 'a_neutral_gender' : 'a_common_gender')),
          newGendered: this.translateService.instant(dialogKey + "." + (isTypeNeutralGender ? 'new_neutral_gender' : 'new_common_gender')),
          type: this.translateService.instant(dialogKey + ".entity." + type),
          action: this.translateService.instant(dialogKey + action)
        }
    );
  }

  public openDialogs() {
    return this.matDialog.openDialogs.length;
  }

  openConfirmationDialog(data: ConfirmationDialogData): MatDialogRef<ConfirmationDialogComponent> {
    return this.open(ConfirmationDialogComponent, {
      panelClass: CONFIRMATION_DIALOG_CLASS,
      autoFocus: 'first-tabbable',
      data: data,
    });
  }

  openClickableDemoStepGuideConfirmationDialog(): MatDialogRef<ConfirmationDialogComponent> {
    const data: ConfirmationDialogData = {
      title: "before_start_dialog.title",
      description: "before_start_dialog.description",
      affirmText: "before_start_dialog.affirm",
      hideCancelButton: true,
      color: ConfirmationDialogColor.Light,
    };

    return this.openConfirmationDialog(data);
  }

  closeAll() {
    this.matDialog.closeAll();
  }

  private dialogRefKey<T>(component: ComponentType<T> | TemplateRef<T>, configs?: MatDialogConfig): string {
    let componentType = "";
    if (component instanceof TemplateRef) {
      componentType = typeof component;
    } else {
      const t = component as ComponentType<T>;
      componentType = t.name;
    }
    return componentType + JSON.stringify(configs);
  }
}
